import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initAnalytics } from './utils/analytics';

// ── Sentry ────────────────────────────────────────────────────────
// Initialised before anything else so it can attach to React's error
// boundaries and unhandled-promise listeners. No-op if the DSN env
// var isn't set (e.g. local dev without the var).
//
// CRA env-var rule: must be prefixed REACT_APP_ to be exposed to the
// client bundle. On Railway the env var must be named
// REACT_APP_SENTRY_DSN_FRONTEND (NOT SENTRY_DSN_FRONTEND).
//
// Session replay was REMOVED 2026-08-17. It previously captured a masked
// DOM recording on error, but masking is best-effort and the canvases hold
// confidential SME business data, so no screen recording happens at all now.
// Errors and performance traces still report normally.
//
// Do NOT re-add Sentry.replayIntegration() without a data-protection review.
if (process.env.REACT_APP_SENTRY_DSN_FRONTEND) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN_FRONTEND,
    environment: process.env.NODE_ENV || 'production',
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    // Belt-and-braces: even if a replay integration is reintroduced by a
    // dependency default, never sample a session or an error into a replay.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
}

// Global auth header — attaches the user's JWT (when present in
// localStorage) to every outgoing axios request. Routes that don't
// require auth (login, register, beta-signup, etc.) simply ignore
// the header on the backend, so this is safe to apply broadly.
// Won't override an explicit Authorization header set on the call
// site (e.g. the 2FA-pending tempToken flow in Login.js).
axios.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('guruone_token');
    if (token && !(config.headers && config.headers.Authorization)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // localStorage can throw in some private-browsing modes — fall through.
  }
  return config;
});

const root = ReactDOM.createRoot(document.getElementById('root'));
initAnalytics();
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div style={{ padding: 40, fontFamily: 'Inter, sans-serif', maxWidth: 600, margin: '40px auto' }}>
          <h2>Something went wrong.</h2>
          <p>We have been notified and are looking into it. Please refresh the page to continue.</p>
          <button
            onClick={resetError}
            style={{ marginTop: 16, padding: '10px 20px', cursor: 'pointer' }}
          >
            Try again
          </button>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
